import type { ReactNode } from "react";
import { Activity, GitCompareArrows, ShieldAlert, SlidersHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";
import { ROLE_META, type PositionRole, type Tone } from "@/lib/semantics";
import type {
  AnalyticsOverviewResponse,
  DailyReviewResponse,
} from "../../api/client";
import Panel from "../common/Panel";
import Stat from "../common/Stat";
import { ToneBadge } from "../common/SemanticBadge";
import { DailyPnlColumns, EquityChart, ShareBars, fmtEur } from "./charts";

/* ============================================================
 * Analytique — le juge du forward-test, pas une brochure.
 * KPI (combien) → equity (comment) → ères (quel jeu de params
 * gagne) → mécanique (sorties / refus / risque §7). La liste
 * des cycles vit dans Historique (un fait = une maison) ; la
 * pédagogie d'onboarding a été purgée (utilisateur unique).
 * ============================================================ */

const WINDOWS = [
  { days: 7, label: "7j" },
  { days: 30, label: "30j" },
  { days: 90, label: "90j" },
  { days: 0, label: "Tout" },
] as const;

export function WindowPicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div className="border-border-strong bg-surface-2/60 inline-flex items-center rounded-lg border p-0.5" role="group" aria-label="Fenêtre temporelle">
      {WINDOWS.map((w) => (
        <button
          key={w.days}
          type="button"
          data-testid={`window-${w.label}`}
          aria-pressed={days === w.days}
          onClick={() => onChange(w.days)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            days === w.days
              ? "bg-overlay text-foreground shadow-sm ring-1 ring-white/10"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

/* ── Rangée KPI — les chiffres de tête, un coup d'œil ─────────────────────── */

export function KpiRow({
  overview,
  daily,
}: {
  overview: AnalyticsOverviewResponse;
  daily: DailyReviewResponse | null;
}) {
  const k = overview.kpis;
  const netTone: Tone = k.netPnlEur > 0 ? "buy" : k.netPnlEur < 0 ? "sell" : "neutral";
  const todayPnl = daily?.realizedPnlEur ?? null;
  const todayTone: Tone = todayPnl == null ? "neutral" : todayPnl > 0 ? "buy" : todayPnl < 0 ? "sell" : "neutral";
  const winRate = k.winRatePct;
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="kpi-row">
      <KpiCard emphasis>
        <Stat
          label="P&L net"
          value={fmtEur(k.netPnlEur, { sign: true })}
          tone={netTone}
          size="lg"
          valueClassName="text-3xl"
          hint={`${k.settled} cycle${k.settled === 1 ? "" : "s"} réglé${k.settled === 1 ? "" : "s"}${k.cycles > k.settled ? " · 1 en cours" : ""}`}
        />
      </KpiCard>
      <KpiCard>
        <Stat
          label="Aujourd'hui (Paris)"
          value={fmtEur(todayPnl, { sign: true })}
          tone={todayTone}
          hint={daily ? `${daily.wins}G / ${daily.losses}P` : undefined}
        />
      </KpiCard>
      <KpiCard>
        <Stat
          label="Taux de gain"
          value={winRate == null ? "—" : `${winRate.toFixed(1)}%`}
          hint={`${k.wins}G / ${k.losses}P`}
        />
      </KpiCard>
      <KpiCard>
        <Stat
          label="Moyenne / cycle"
          value={fmtEur(k.avgPnlEur, { sign: true })}
          tone={k.avgPnlEur == null ? "neutral" : k.avgPnlEur >= 0 ? "buy" : "sell"}
          hint={k.profitFactor == null ? "profit factor —" : `profit factor ${k.profitFactor.toFixed(2)}`}
        />
      </KpiCard>
      <KpiCard>
        <Stat
          label="Drawdown max"
          value={fmtEur(k.maxDrawdownEur === 0 ? 0 : -k.maxDrawdownEur)}
          tone={k.maxDrawdownEur > 0 ? "sell" : "neutral"}
          hint={`meilleur ${fmtEur(k.bestPnlEur, { sign: true })} · pire ${fmtEur(k.worstPnlEur, { sign: true })}`}
        />
      </KpiCard>
    </div>
  );
}

function KpiCard({ children, emphasis }: { children: ReactNode; emphasis?: boolean }) {
  return (
    <div
      className={cn(
        "glass bezel rounded-xl border p-4",
        emphasis ? "border-info/40 ring-1 ring-info/15" : "border-border/80",
      )}
    >
      {children}
    </div>
  );
}

/* ── Equity — la courbe + sa compagne quotidienne ─────────────────────────── */

export function EquityPanel({ overview }: { overview: AnalyticsOverviewResponse }) {
  return (
    <Panel
      title="Courbe d'equity"
      icon={<Activity />}
      aria-label="Courbe d'equity"
      testId="equity-panel"
    >
      <EquityChart points={overview.equity} />
      <div className="border-border/60 mt-3 border-t pt-3">
        <DailyPnlColumns days={overview.daily} />
      </div>
    </Panel>
  );
}

/* ── Mécanique V6 : rôles / sorties / refus ───────────────────────────────── */

export function RolesPanel({ overview }: { overview: AnalyticsOverviewResponse }) {
  // Ligne de stat par rôle (l'ex-graphe à 2 barres n'apportait rien). Les rôles
  // historiques (DEFENSE, EXTENSION) n'apparaissent que si la fenêtre en contient.
  const rows = overview.byRole;
  return (
    <Panel
      title="P&L par rôle"
      icon={<SlidersHorizontal />}
      aria-label="P&L par rôle"
      testId="role-panel"
    >
      {rows.length === 0 ? (
        <div className="text-muted-foreground text-sm">Aucune position fermée dans cette fenêtre.</div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((r) => {
            const meta = ROLE_META[r.role as PositionRole];
            const historical = r.role === "DEFENSE" || r.role === "EXTENSION";
            return (
              <li key={r.role} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-foreground truncate">
                  {meta?.label ?? r.role}
                  {historical && <span className="text-dim ml-1.5 text-[10px]">hist.</span>}
                </span>
                <span className="flex items-baseline gap-2 font-mono text-xs tabular-nums">
                  <span className="text-muted-foreground">{r.positions} pos</span>
                  <span
                    className={cn(
                      "font-semibold",
                      r.pnlEur > 0 ? "text-buy" : r.pnlEur < 0 ? "text-sell" : "text-muted-foreground",
                    )}
                  >
                    {fmtEur(r.pnlEur, { sign: true })}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

const EXIT_LABELS: Record<string, string> = {
  TRAILING_STOP_HIT: "Trail (sortie normale)",
  INITIAL_SL_HIT: "SL catastrophe",
  BREAK_EVEN_STOP_HIT: "Stop break-even",
  BREAK_EVEN_PLUS_STOP_HIT: "Stop break-even+",
  MANUAL_USER_CLOSE: "Clôture manuelle",
  USER_STOP: "Clôture manuelle",
  MARKET_CLOSED_FLATTEN: "Flatten de fermeture",
  CIRCUIT_MARKET_CLOSED: "Circuit marché fermé",
  CIRCUIT_MANUAL_EMERGENCY_STOP: "Arrêt d'urgence",
  DEFENSIVE_HEDGE_CLOSED: "Hedge défensif (hist.)",
  BROKER_CLOSED_UNKNOWN: "Broker (non classé)",
};

export function ExitMixPanel({ overview }: { overview: AnalyticsOverviewResponse }) {
  const items = overview.byExit.map((e) => ({
    key: e.exitReason,
    label: EXIT_LABELS[e.exitReason] ?? e.exitReason,
    count: e.positions,
    detail: fmtEur(e.pnlEur, { sign: true }),
    emphasis: e.exitReason === "TRAILING_STOP_HIT",
  }));
  return (
    <Panel
      title="Mix de sorties"
      icon={<Activity />}
      aria-label="Mix de sorties"
      testId="exit-panel"
    >
      <ShareBars items={items} testId="exit-bars" />
    </Panel>
  );
}

const SKIP_LABELS: Record<string, string> = {
  SKIP_CONSECUTIVE_STOPS: "Verrou stops consécutifs",
  SKIP_BUDGET: "Budget risque §7",
  SKIP_MARGIN: "Marge broker",
  SKIP_MIN_BALANCE: "Solde minimum",
  SKIP_MAX_TOTAL: "Cap du livre",
  CIRCUIT_STREAM_HEALTH: "Santé du flux",
  CIRCUIT_MARKET_CLOSED: "Marché fermé",
  CIRCUIT_MANUAL_EMERGENCY_STOP: "Arrêt d'urgence",
};

export function GatesPanel({ overview }: { overview: AnalyticsOverviewResponse }) {
  const total = overview.skips.reduce((s, x) => s + x.count, 0);
  return (
    <Panel
      title="Entrées refusées"
      icon={<ShieldAlert />}
      aside={<span className="font-mono tabular-nums">{total.toLocaleString("en-US")}</span>}
      aria-label="Entrées refusées"
      testId="gates-panel"
    >
      {overview.skips.length === 0 ? (
        <div className="text-muted-foreground text-sm">Rien de bloqué dans cette fenêtre.</div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {overview.skips.map((s) => (
            <li key={s.decisionType} className="flex items-baseline justify-between gap-3 text-sm">
              <span className="text-foreground truncate" title={s.decisionType}>
                {SKIP_LABELS[s.decisionType] ?? s.decisionType}
              </span>
              <span className="text-muted-foreground font-mono tabular-nums">
                {s.count.toLocaleString("en-US")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ── Ères de paramètres — le tableau des scores du forward-test ───────────── */

export function VersionComparePanel({ overview }: { overview: AnalyticsOverviewResponse }) {
  const rows = overview.byVersion;
  return (
    <Panel
      title="Ères de paramètres"
      icon={<GitCompareArrows />}
      aside={<span>toutes périodes</span>}
      aria-label="Ères de paramètres"
      testId="version-panel"
    >
      {rows.length === 0 ? (
        <div className="text-muted-foreground text-sm">Aucun cycle enregistré.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((v, i) => (
            <div
              key={v.versionId ?? `null-${i}`}
              className={cn(
                "rounded-lg border p-3",
                i === 0 ? "border-info/40 bg-info-soft/30" : "border-border/70",
              )}
              data-testid={`version-row-${v.versionId ?? "none"}`}
            >
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <span className="text-foreground truncate font-mono text-xs font-semibold">
                  {v.label ?? (v.versionId == null ? "non estampillé" : `v${v.versionId}`)}
                  {/* désambiguïse les ères de même libellé (des sets peuvent différer hors libellé) */}
                  {v.label != null && v.versionId != null && (
                    <span className="text-dim ml-1.5 font-normal">#{v.versionId}</span>
                  )}
                </span>
                {i === 0 && <ToneBadge tone="info">actuelle</ToneBadge>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Stat label="P&L net" size="sm"
                  value={fmtEur(v.netPnlEur, { sign: true })}
                  tone={v.netPnlEur > 0 ? "buy" : v.netPnlEur < 0 ? "sell" : "neutral"} />
                <Stat label="Taux de gain" size="sm"
                  value={v.winRatePct == null ? "—" : `${v.winRatePct.toFixed(0)}%`}
                  hint={`${v.settled} réglés`} />
                <Stat label="WCL max" size="sm" value={fmtEur(v.maxWclEur)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
